import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DatePicker, Dropdown } from '@com-delta-omnichannel/mach-core-js';

import { STAYS_ERROR_TYPES } from '../mocks/stays.constants';
import {
  StaysComponents,
  StaysFormErrors,
  StaysWidget,
  StaysWidgetForm
} from '../models/stays-widget.interfaces';
import { ComponentLifecycleHelperService } from './component-lifecycle-helper.service';

@Injectable({
  providedIn: 'root'
})
export class ComponentLifecycleService extends ComponentLifecycleHelperService {
  initializeWidget(
    content: StaysWidget,
    form: FormGroup<StaysWidgetForm>,
    isSubmitted: () => boolean,
    onDatePickerClose?: () => void
  ): {
    datePickerInstance: DatePicker;
    dropDownTravelerOptions: Dropdown;
    dropDownRoomOptions: Dropdown;
  } {
    const components = this.initService.initializeComponents(content);
    this.setupEventListeners(
      components,
      form,
      content,
      isSubmitted,
      onDatePickerClose
    );
    return components;
  }

  private setupEventListeners(
    components: StaysComponents,
    form: FormGroup<StaysWidgetForm>,
    content: StaysWidget,
    isSubmitted: () => boolean,
    onDatePickerClose?: () => void
  ): void {
    this.dateService.setupDateChangeListener(
      form,
      components.datePickerInstance,
      content,
      'range-date-display',
      onDatePickerClose
    );
    this.setupDropdownListeners(components, form, isSubmitted, content);
  }

  updateDestination(
    form: FormGroup<StaysWidgetForm>,
    content: StaysWidget,
    destination: string
  ): string {
    if (!form) return '';

    content.locationContent.label = destination;
    form.patchValue({ location: destination });

    return this.setDestinationError(form, content);
  }

  private setupDropdownListeners(
    components: StaysComponents,
    form: FormGroup<StaysWidgetForm>,
    isSubmitted: () => boolean,
    content: StaysWidget
  ): void {
    components.dropDownRoomOptions.onChange((value: { value: string }) => {
      form.patchValue({ numberOfRooms: +value.value });
      this.eventService.emitErrorCountChange();

      if (isSubmitted()) {
        const travelerError = this.dropdownService.setPassengerError(
          form,
          content,
          STAYS_ERROR_TYPES.ROOM_MISMATCH
        );
        const roomError = this.dropdownService.setRoomsError(
          form,
          content,
          STAYS_ERROR_TYPES.ROOM_TRAVELER_MISMATCH
        );
        this.eventService.emitPassengerError(travelerError);
        this.eventService.emitRoomError(roomError);
      }
    });

    components.dropDownTravelerOptions.onChange((value: { value: string }) => {
      const travelerCount = +value.value;
      form.patchValue({ numberOfTravelers: travelerCount });
      this.eventService.emitErrorCountChange();

      if (isSubmitted()) {
        const travelerError = this.dropdownService.setPassengerError(
          form,
          content,
          STAYS_ERROR_TYPES.ROOM_MISMATCH
        );

        const roomError = this.dropdownService.setRoomsError(
          form,
          content,
          STAYS_ERROR_TYPES.ROOM_TRAVELER_MISMATCH
        );
        this.eventService.emitPassengerError(travelerError);
        this.eventService.emitRoomError(roomError);
      }
    });

    components.dropDownTravelerOptions.setValue(
      content.dropDownTravelerOption.items[0].value
    );
    components.dropDownRoomOptions.setValue(
      content.dropDownRoomOption.items[0].value
    );
  }

  processFormSubmission(
    form: FormGroup<StaysWidgetForm>,
    content: StaysWidget
  ): StaysFormErrors {
    return {
      destinationErrorLabel: this.setDestinationError(form, content),
      dateError: this.setDateError(form, content),
      maxDatesError: this.dateService.setMaxDatesError(
        form,
        content,
        STAYS_ERROR_TYPES.MAX_DATE_MISMATCH
      ),
      travelerError: this.dropdownService.setPassengerError(
        form,
        content,
        STAYS_ERROR_TYPES.ROOM_MISMATCH
      ),
      roomsError: this.dropdownService.setRoomsError(
        form,
        content,
        STAYS_ERROR_TYPES.ROOM_TRAVELER_MISMATCH
      )
    };
  }
}
